import { useEffect, useState } from "react";

import {
  RadioButton,
  TextArea,
  Table,
  TableMetadata,
  Typography,
  useTable,
  Label,
} from "@king-ict/eupisi-ui/components";
import { CodeBook } from "@king-ict/eupisi-ui/models/Common/CodeBook";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { getMinistryCommitteeById } from "../../api/Committee/MinistryCommitteeApi";
import { getSchoolYearCurrent } from "../../api/SchoolYear/SchoolYearApi";
import { ToolbarTop, FormCard, ContentArea } from "../../components";
import {
  MinistryCommittee,
  CommitteeStatus,
  CommitteeEmployee,
} from "../../models/Committee";
import { ImportantDateEventEnum } from "../../models/ImportantDate/ImportantDateEventEnum";
import {
  ministryCommitteeEditUrl,
  ministryCommitteeListRoute,
} from "../../routes/routes";
import { FormatUtils, MsgUtils } from "../../utils";
import { getCommitteeEmployeeDisplayName } from "./utils";

export function MinistryCommitteeDetails() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();

  const createDefaultItem = (): MinistryCommittee => {
    return {
      id: 0,
      statusId: CommitteeStatus.NotDefined,
      isSelected: false,
      statusName: "",
    };
  };
  const [item, setItem] = useState<MinistryCommittee>(createDefaultItem);
  const [tableData, setTableData] = useState<CommitteeEmployee[]>([]);
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const tableMetadata: TableMetadata<CommitteeEmployee> = {
    rowKey: "id",
    columns: [
      {
        headerClassName: "col-4",
        columnKey: "expertCommissionRole",
        header: t("common.expertCommission.role"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{item.committeeRole?.name}</div>;
        },
      },
      {
        headerClassName: "col-6",
        columnKey: "employee",
        header: t("common.expertCommission.employee"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{getCommitteeEmployeeDisplayName(item)}</div>;
        },
      },
      {
        headerClassName: "col-4",
        columnKey: "activeTo",
        header: t("common.expertCommission.activeTo"),
        accessor: (item) => {
          return <div>{FormatUtils.formatDate(item.deactivationDate)}</div>;
        },
      },
      {
        headerClassName: "col-1",
        columnKey: "expertCommissionPosition",
        header: t("common.expertCommission.position"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{item.committeePosition?.name}</div>;
        },
      },
      {
        headerClassName: "col-1",
        columnKey: "president",
        header: t("common.expertCommission.president"),
        accessor: (emp: CommitteeEmployee) => {
          return (
            <div className="pt-2 ml-3">
              <RadioButton
                ariaLabel="item.president"
                key={emp.committeeRole?.id}
                name={"item.president"}
                checked={emp.isPresident}
                onChange={() => {}}
                value={emp.committeeRole!.id}
                disabled
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };

  const [tableProps] = useTable({
    metadata: tableMetadata,
    data: tableData,
    pagination: false,
    paginationPage: undefined,
    paginationPageSize: undefined,
    totalItems: 1,
    defaultSort: {},
  });
  const [statuses] = useState<CodeBook[]>(
    MsgUtils.getEnumToCodeBook(CommitteeStatus, "CommitteeStatus")
  );

  useEffect(() => {
    (async () => {
      const schoolYear = await getSchoolYearCurrent();
      if (schoolYear) {
        const importantDate = schoolYear.importantDates.find(
          (val) =>
            val.eventId === ImportantDateEventEnum.InstitutionAdministration
        );
        if (importantDate) {
          const exactlyNow = new Date(Date.now());
          const dateFrom = new Date(importantDate.dateTimeFrom);
          const dateTo = new Date(importantDate.dateTimeTo);
          if (dateFrom <= exactlyNow && dateTo >= exactlyNow) {
            setIsEditable(true);
          }
        }
      }
      if (!isNaN(+id) && +id > 0) {
        var result = await getMinistryCommitteeById(+id);
        if (result) {
          setItem(result);
          setTableData(result.employees!);
        }
      }
    })();
  }, [id]);

  const goBack = () => {
    history.push(ministryCommitteeListRoute);
  };
  const handleEditClicked = () => {
    history.push(ministryCommitteeEditUrl(+id));
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={isEditable ? "show" : "disable"}
        pageTitle={t("common.expertCommission.ministryPageNameEdit")}
        onClickEdit={handleEditClicked}
        onClickBack={goBack}
      />

      <FormCard>
        <Row>
          <Col md={12}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.ministry")}
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.dateFromDateTo")}
            </Typography>
          </Col>
          <Col md={4}>
            <Typography variant="base" color="light">
              {item.dateFrom ? hrDate(item.dateFrom) : ""} -{" "}
              {item.dateTo ? hrDate(item.dateTo) : ""}
            </Typography>
          </Col>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.status")}
            </Typography>
          </Col>
          <Col md={4}>
            <Label
              color={FormatUtils.getCommitteeStatusColor(item.statusId)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {statuses.find((v) => v.id === item.statusId)?.name}
            </Label>
          </Col>
        </Row>
      </FormCard>

      <FormCard>
        <Table {...tableProps} />
      </FormCard>

      <FormCard>
        <Row>
          <Col md={12}>
            <TextArea
              ariaLabel="item.note"
              label={t("common.expertCommission.note")}
              name={"item.note"}
              value={item.note}
              onChange={() => {}}
              maxLength={5000}
              disabled
            />
          </Col>
        </Row>
      </FormCard>
    </ContentArea>
  );
}
